import { createSlice } from '@reduxjs/toolkit';

// Processing step names (match backend)
export const PROCESS_STEPS = {
  BRAND_SAVED: 'brand_saved',
  FETCHING_BACKLINKS: 'fetching_backlinks',
  BACKLINKS_COMPLETE: 'backlinks_complete',
  CLASSIFYING_URLS: 'classifying_urls',
  CLASSIFICATION_COMPLETE: 'classification_complete',
  CHECKING_BRAND_PAGES: 'checking_brand_pages',
  BRAND_PAGES_COMPLETE: 'brand_pages_complete',
  CHECKING_AFFILIATE_PAGES: 'checking_affiliate_pages',
  AFFILIATE_PAGES_COMPLETE: 'affiliate_pages_complete',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

// Human-readable step labels
export const STEP_LABELS = {
  [PROCESS_STEPS.BRAND_SAVED]: 'Brand saved',
  [PROCESS_STEPS.FETCHING_BACKLINKS]: 'Fetching backlinks from SEMrush',
  [PROCESS_STEPS.BACKLINKS_COMPLETE]: 'Backlinks fetched',
  [PROCESS_STEPS.CLASSIFYING_URLS]: 'Classifying URLs with AI',
  [PROCESS_STEPS.CLASSIFICATION_COMPLETE]: 'URL classification complete',
  [PROCESS_STEPS.CHECKING_BRAND_PAGES]: 'Checking brand website pages',
  [PROCESS_STEPS.BRAND_PAGES_COMPLETE]: 'Brand pages checked',
  [PROCESS_STEPS.CHECKING_AFFILIATE_PAGES]: 'Checking affiliate pages for terms',
  [PROCESS_STEPS.AFFILIATE_PAGES_COMPLETE]: 'Affiliate pages checked',
  [PROCESS_STEPS.COMPLETED]: 'Processing complete',
  [PROCESS_STEPS.FAILED]: 'Processing failed'
};

const initialState = {
  // Track processing status for each brand by ID
  processingBrands: {},
};

const termsCheckerSlice = createSlice({
  name: 'termsChecker',
  initialState,
  reducers: {
    // Start processing for a brand
    startBrandProcessing: (state, action) => {
      const { brandId, brandName } = action.payload;
      state.processingBrands[brandId] = {
        isProcessing: true,
        progress: 'Starting processing...',
        brandName: brandName,
        startTime: Date.now(),
        currentStep: PROCESS_STEPS.BRAND_SAVED,
        overallProgress: 0,
        steps: {
          [PROCESS_STEPS.BRAND_SAVED]: { status: 'completed', message: 'Brand saved', progress: 100 }
        },
        error: null
      };
    },

    // Update from API poll response
    updateBrandProcessingFromApi: (state, action) => {
      const { brandId, data } = action.payload;
      if (!state.processingBrands[brandId]) {
        state.processingBrands[brandId] = {
          isProcessing: true,
          startTime: Date.now(),
          steps: {},
          error: null
        };
      }

      const brand = state.processingBrands[brandId];
      brand.currentStep = data.currentStep;
      brand.overallProgress = data.overallProgress || 0;
      brand.progress = STEP_LABELS[data.currentStep] || data.currentStep;
      brand.error = data.error || null;

      if (data.steps) {
        for (const step of data.steps) {
          brand.steps[step.name] = {
            status: step.status,
            message: step.message,
            progress: step.progress || 0,
            itemsTotal: step.itemsTotal || 0,
            itemsProcessed: step.itemsProcessed || 0,
            itemsFailed: step.itemsFailed || 0
          };
        }
      }

      if (data.processingStatus === 'completed') {
        brand.isProcessing = false;
        brand.progress = 'Complete';
        brand.overallProgress = 100;
      } else if (data.processingStatus === 'failed') {
        brand.isProcessing = false;
        brand.progress = 'Failed';
        brand.error = data.error;
      } else {
        brand.isProcessing = true;
      }
    },

    // Simple progress update
    updateBrandProgress: (state, action) => {
      const { brandId, progress, currentStep, overallProgress } = action.payload;
      if (state.processingBrands[brandId]) {
        if (progress) state.processingBrands[brandId].progress = progress;
        if (currentStep) state.processingBrands[brandId].currentStep = currentStep;
        if (overallProgress !== undefined) state.processingBrands[brandId].overallProgress = overallProgress;
      }
    },

    // Mark processing as complete
    completeBrandProcessing: (state, action) => {
      const { brandId } = action.payload;
      if (state.processingBrands[brandId]) {
        state.processingBrands[brandId].isProcessing = false;
        state.processingBrands[brandId].progress = 'Complete';
        state.processingBrands[brandId].currentStep = PROCESS_STEPS.COMPLETED;
        state.processingBrands[brandId].overallProgress = 100;
      }
    },

    // Mark processing as failed
    failBrandProcessing: (state, action) => {
      const { brandId, error } = action.payload;
      if (state.processingBrands[brandId]) {
        state.processingBrands[brandId].isProcessing = false;
        state.processingBrands[brandId].progress = 'Failed';
        state.processingBrands[brandId].currentStep = PROCESS_STEPS.FAILED;
        state.processingBrands[brandId].error = error;
      }
    },

    // Remove brand from processing tracker
    removeBrandProcessing: (state, action) => {
      const { brandId } = action.payload;
      delete state.processingBrands[brandId];
    },

    // Clear all processing
    clearAllProcessing: (state) => {
      state.processingBrands = {};
    },
  },
});

export const {
  startBrandProcessing,
  updateBrandProcessingFromApi,
  updateBrandProgress,
  completeBrandProcessing,
  failBrandProcessing,
  removeBrandProcessing,
  clearAllProcessing,
} = termsCheckerSlice.actions;

export default termsCheckerSlice.reducer;

// Selectors
export const selectProcessingBrands = (state) => state.termsChecker.processingBrands;

export const selectIsBrandProcessing = (brandId) => (state) =>
  state.termsChecker.processingBrands[brandId]?.isProcessing || false;

export const selectBrandProgress = (brandId) => (state) =>
  state.termsChecker.processingBrands[brandId]?.progress || '';

export const selectBrandOverallProgress = (brandId) => (state) =>
  state.termsChecker.processingBrands[brandId]?.overallProgress || 0;

export const selectBrandCurrentStep = (brandId) => (state) =>
  state.termsChecker.processingBrands[brandId]?.currentStep || null;

export const selectBrandProcessingSteps = (brandId) => (state) =>
  state.termsChecker.processingBrands[brandId]?.steps || {};

export const selectBrandProcessingError = (brandId) => (state) =>
  state.termsChecker.processingBrands[brandId]?.error || null;
